import React from 'react';

/**
 * Generic render-error boundary. Catches crashes in its subtree so one piece of
 * malformed data can't white-screen the page / show the dev error overlay.
 *
 * Usage:
 *   <ErrorBoundary fallback={<div>…</div>}>{children}</ErrorBoundary>
 *
 * `resetKey` — when this prop changes, the boundary clears its error state and
 * retries rendering. Pass the id of the thing being shown (e.g. a userId) so
 * opening a DIFFERENT item after one failed renders fresh instead of staying
 * stuck on the fallback.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Keep the detail in the console for debugging; the UI shows the fallback.
    console.error('ErrorBoundary caught an error:', error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
